/**
 * Gestión de usuarios y roles. Vive dentro de /settings y solo la ve un admin.
 *
 * Antes solo existía el primer administrador que crea /setup con la tabla
 * vacía: no había forma de dar de alta a un inspector o a un mecánico, así que
 * la aplicación se usaba con una cuenta compartida y `inspector_name` mentía en
 * cada inspección.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { UserPlus, Users } from 'lucide-react'
import {
  ApiError,
  createUser,
  deactivateUser,
  listUsers,
  patchUser,
  type ManagedUser,
} from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Field } from '@/components/ui/field'
import { StatusPill } from '@/components/ui/status-pill'
import { cn } from '@/lib/utils'

export const ROLES = ['admin', 'manager', 'inspector', 'mechanic'] as const
export type Role = (typeof ROLES)[number]

/** Qué hace cada rol, en la propia pantalla: nadie va a buscarlo a la documentación. */
const ROLE_HELP: Record<Role, string> = {
  admin: 'Todo, incluida esta pantalla',
  manager: 'Flota, gastos, mantenimientos y proveedores',
  inspector: 'Crear y corregir inspecciones',
  mechanic: 'Órdenes de trabajo y componentes',
}

function RolePicker({
  value,
  onChange,
  idPrefix,
}: {
  value: Role[]
  onChange: (roles: Role[]) => void
  idPrefix: string
}) {
  return (
    <fieldset className="space-y-1.5">
      <legend className="text-sm font-medium">Roles</legend>
      {ROLES.map((role) => (
        <label key={role} className="flex min-h-11 items-center gap-2 text-sm">
          <input
            type="checkbox"
            id={`${idPrefix}-${role}`}
            checked={value.includes(role)}
            onChange={(e) =>
              onChange(
                e.target.checked ? [...value, role] : value.filter((r) => r !== role),
              )
            }
            className="size-4"
          />
          <span className="font-medium">{role}</span>
          <span className="text-xs text-muted-foreground">· {ROLE_HELP[role]}</span>
        </label>
      ))}
    </fieldset>
  )
}

export function UsersPanel({ currentUserId }: { currentUserId: string }) {
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)
  const query = useQuery({ queryKey: ['users'], queryFn: listUsers })
  const users = query.data?.users ?? []

  return (
    <Card className="lg:col-span-2">
      <CardHeader
        title="Usuarios y roles"
        action={
          <Button type="button" size="sm" onClick={() => setCreating((v) => !v)}>
            <UserPlus className="size-4" aria-hidden />
            {creating ? 'Cerrar' : 'Nuevo usuario'}
          </Button>
        }
      />
      <CardBody className="space-y-3">
        {creating && (
          <UserCreateForm
            onDone={async () => {
              setCreating(false)
              await qc.invalidateQueries({ queryKey: ['users'] })
            }}
          />
        )}

        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : users.length === 0 ? (
          <EmptyState icon={Users} title="Sin usuarios" />
        ) : (
          <div className="divide-y divide-border rounded-xl border border-border">
            {users.map((u) => (
              <UserRow key={u.id} user={u} isSelf={u.id === currentUserId} />
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

function UserCreateForm({ onDone }: { onDone: () => void }) {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [roles, setRoles] = useState<Role[]>(['inspector'])
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, string>>({})

  const create = useMutation({
    mutationFn: () => createUser({ fullName, email, phone: phone || null, password, roles }),
    onSuccess: onDone,
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message)
        setFields(err.fields ?? {})
      } else setError('No se pudo crear.')
    },
  })

  return (
    <div className="space-y-3 rounded-xl border border-border p-3">
      {error && (
        <p role="alert" className="text-sm font-medium text-danger">
          {error}
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Nombre completo"
          required
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          error={fields.fullName}
        />
        <Field
          label="Email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fields.email}
        />
        <Field label="Teléfono" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <Field
          label="Contraseña"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fields.password}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Mínimo 12 caracteres, con mayúsculas, minúsculas y números — los mismos requisitos que
        para el primer administrador.
      </p>
      <RolePicker value={roles} onChange={setRoles} idPrefix="new" />
      <Button
        type="button"
        className="w-full"
        loading={create.isPending}
        disabled={roles.length === 0}
        onClick={() => {
          setError(null)
          setFields({})
          create.mutate()
        }}
      >
        Crear usuario
      </Button>
    </div>
  )
}

function UserRow({ user, isSelf }: { user: ManagedUser; isSelf: boolean }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [fullName, setFullName] = useState(user.fullName)
  const [email, setEmail] = useState(user.email)
  const [phone, setPhone] = useState(user.phone ?? '')
  const [roles, setRoles] = useState<Role[]>(user.roles as Role[])
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  const refresh = () => qc.invalidateQueries({ queryKey: ['users'] })
  const fail = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : 'No se pudo completar la operación.')

  const save = useMutation({
    mutationFn: () =>
      patchUser(user.id, {
        fullName,
        email,
        phone: phone || null,
        roles,
        // La contraseña solo viaja si se escribió algo: mandarla vacía la
        // reescribiría con una cadena que nadie conoce.
        ...(password ? { password } : {}),
      }),
    onSuccess: async () => {
      setEditing(false)
      setPassword('')
      setError(null)
      await refresh()
    },
    onError: fail,
  })

  const toggleActive = useMutation({
    mutationFn: () =>
      user.active ? deactivateUser(user.id) : patchUser(user.id, { active: true }),
    onSuccess: async () => {
      setError(null)
      await refresh()
    },
    onError: fail,
  })

  return (
    <div className="p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">
            {user.fullName}
            {isSelf && <span className="ml-2 text-xs text-muted-foreground">(tú)</span>}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {user.email}
            {user.lastLoginAt
              ? ` · último acceso ${new Date(user.lastLoginAt).toLocaleDateString()}`
              : ' · sin accesos'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user.roles.map((r) => (
            <StatusPill key={r} status={r} tone="info" />
          ))}
          <StatusPill status={user.active ? 'Active' : 'Inactivo'} />
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" onClick={() => setEditing((v) => !v)}>
          {editing ? 'Cerrar' : 'Editar'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={user.active ? 'outline' : 'primary'}
          loading={toggleActive.isPending}
          onClick={() => toggleActive.mutate()}
        >
          {user.active ? 'Desactivar' : 'Reactivar'}
        </Button>
      </div>

      {error && (
        <p role="alert" className="mt-2 text-xs font-medium text-danger">
          {error}
        </p>
      )}

      {editing && (
        <div className={cn('mt-3 space-y-3 rounded-lg border border-border p-3')}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Nombre completo"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
            <Field
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Field label="Teléfono" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <Field
              label="Nueva contraseña"
              type="password"
              value={password}
              placeholder="Dejar vacío para no cambiarla"
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <RolePicker value={roles} onChange={setRoles} idPrefix={user.id} />
          <Button
            type="button"
            className="w-full"
            loading={save.isPending}
            disabled={roles.length === 0}
            onClick={() => {
              setError(null)
              save.mutate()
            }}
          >
            Guardar cambios
          </Button>
        </div>
      )}
    </div>
  )
}
